/**
 * ProfitReport — owner-only profit dashboard.
 * Shows: total profit summary, profit per loan, per salesman, per month.
 * Gated server-side by shop.internal_pricing_enabled (returns 403 when off).
 */
import React, { useEffect, useState } from "react";
import { api, fmtPKR } from "../lib/api";
import { TrendingUp, Wallet, Users, Calendar, AlertCircle, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";

function StatCard({ icon: Icon, label, value, sub, testid, tone = "emerald" }) {
  const tones = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    slate:   "text-slate-700 bg-slate-50 border-slate-200",
    indigo:  "text-indigo-700 bg-indigo-50 border-indigo-200",
  };
  return (
    <div className={`rounded-md border p-4 ${tones[tone] || tones.emerald}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="font-display font-bold text-2xl mt-1 tabular">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ProfitReport() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/reports/profit")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Profit report load nahi hua"));
  }, []);

  if (err) {
    return (
      <div className="max-w-2xl space-y-3" data-testid="profit-report-error">
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <TrendingUp className="w-7 h-7 text-emerald-600" /> Profit Report
        </h1>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">{err}</div>
            <Link to="/settings" className="text-sm text-amber-800 underline mt-1 inline-block">
              Settings mein jaa kar &quot;Internal Cost &amp; Profit Tracking&quot; enable karein →
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="text-slate-400" data-testid="profit-report-loading">Loading…</div>;

  const s = data.summary || {};
  return (
    <div className="space-y-6" data-testid="profit-report-page">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <TrendingUp className="w-7 h-7 text-emerald-600" /> Profit Report
        </h1>
        <p className="text-slate-500 mt-1">
          Cost &amp; profit tracking — sirf aap (owner) ko visible. Customer aur Agreement PDF mein yeh kabhi nahi dikhe ga.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Wallet} label="Total Profit" value={fmtPKR(s.total_profit)}
                  sub={`Across ${s.loans_with_pricing} loans`} testid="profit-stat-total" />
        <StatCard icon={ShoppingBag} label="Avg / Loan" value={fmtPKR(s.avg_profit_per_loan)}
                  tone="slate" testid="profit-stat-avg" />
        <StatCard icon={TrendingUp} label="Margin" value={s.overall_margin_pct != null ? `${s.overall_margin_pct}%` : "—"}
                  sub={`Cost ${fmtPKR(s.total_cost)} → Sale ${fmtPKR(s.total_sale)}`}
                  tone="indigo" testid="profit-stat-margin" />
        <StatCard icon={Users} label="Total Sale" value={fmtPKR(s.total_sale)}
                  tone="slate" testid="profit-stat-sale" />
      </div>

      {/* By Salesman */}
      <div className="bg-white border border-slate-200 rounded-md" data-testid="profit-by-salesman">
        <div className="px-4 py-3 border-b border-slate-200 font-display font-bold flex items-center gap-2">
          <Users className="w-4 h-4" /> Profit per Salesman
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Salesman</th>
                <th className="text-right px-4 py-2 font-semibold">Loans</th>
                <th className="text-right px-4 py-2 font-semibold">Cost</th>
                <th className="text-right px-4 py-2 font-semibold">Sale</th>
                <th className="text-right px-4 py-2 font-semibold">Profit</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_salesman || []).map((r) => (
                <tr key={r.salesman_id} className="border-t border-slate-100" data-testid={`profit-sales-row-${r.salesman_id}`}>
                  <td className="px-4 py-2 font-medium">{r.salesman_name}</td>
                  <td className="px-4 py-2 text-right tabular">{r.loans}</td>
                  <td className="px-4 py-2 text-right tabular text-slate-600">{fmtPKR(r.cost)}</td>
                  <td className="px-4 py-2 text-right tabular text-slate-600">{fmtPKR(r.sale)}</td>
                  <td className="px-4 py-2 text-right tabular font-bold text-emerald-700">{fmtPKR(r.profit)}</td>
                </tr>
              ))}
              {(data.by_salesman || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Koi cost-priced loan nahi hai.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* By Month */}
      <div className="bg-white border border-slate-200 rounded-md" data-testid="profit-by-month">
        <div className="px-4 py-3 border-b border-slate-200 font-display font-bold flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Monthly Profit
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Month</th>
                <th className="text-right px-4 py-2 font-semibold">Loans</th>
                <th className="text-right px-4 py-2 font-semibold">Cost</th>
                <th className="text-right px-4 py-2 font-semibold">Sale</th>
                <th className="text-right px-4 py-2 font-semibold">Profit</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_month || []).map((r) => (
                <tr key={r.month} className="border-t border-slate-100" data-testid={`profit-month-row-${r.month}`}>
                  <td className="px-4 py-2 font-medium">{r.month}</td>
                  <td className="px-4 py-2 text-right tabular">{r.loans}</td>
                  <td className="px-4 py-2 text-right tabular text-slate-600">{fmtPKR(r.cost)}</td>
                  <td className="px-4 py-2 text-right tabular text-slate-600">{fmtPKR(r.sale)}</td>
                  <td className="px-4 py-2 text-right tabular font-bold text-emerald-700">{fmtPKR(r.profit)}</td>
                </tr>
              ))}
              {(data.by_month || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Abhi koi data nahi.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per Loan */}
      <div className="bg-white border border-slate-200 rounded-md" data-testid="profit-by-loan">
        <div className="px-4 py-3 border-b border-slate-200 font-display font-bold flex items-center gap-2">
          <ShoppingBag className="w-4 h-4" /> Top Loans by Profit
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Product</th>
                <th className="text-left px-4 py-2 font-semibold">Salesman</th>
                <th className="text-left px-4 py-2 font-semibold">Start</th>
                <th className="text-right px-4 py-2 font-semibold">Cost</th>
                <th className="text-right px-4 py-2 font-semibold">Sale</th>
                <th className="text-right px-4 py-2 font-semibold">Profit</th>
                <th className="text-right px-4 py-2 font-semibold">Margin</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_loan || []).slice(0, 50).map((r) => (
                <tr key={r.loan_id} className="border-t border-slate-100" data-testid={`profit-loan-row-${r.loan_id}`}>
                  <td className="px-4 py-2 font-medium">{r.product_name}</td>
                  <td className="px-4 py-2 text-slate-600">{r.salesman_name}</td>
                  <td className="px-4 py-2 text-slate-500">{r.start_date}</td>
                  <td className="px-4 py-2 text-right tabular text-slate-600">{fmtPKR(r.cost_price)}</td>
                  <td className="px-4 py-2 text-right tabular text-slate-600">{fmtPKR(r.sale_price)}</td>
                  <td className="px-4 py-2 text-right tabular font-bold text-emerald-700">{fmtPKR(r.profit_amount)}</td>
                  <td className="px-4 py-2 text-right tabular">{r.margin_pct != null ? `${r.margin_pct}%` : "—"}</td>
                </tr>
              ))}
              {(data.by_loan || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Koi cost-priced loan nahi hai abhi.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {(data.by_loan || []).length > 50 && (
          <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100">
            Top 50 loans by profit shown. Full list export jald aayega.
          </div>
        )}
      </div>
    </div>
  );
}
